import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { HotDoc } from "~app/common/components/hot-doc/hotdoc/doc";
import { InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { DataExport } from "~app/common/services/dataExport";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { IProduktdefinitionBasdata } from "../../../models/ProduktdefinitionModels";
import { ProduktdefinitionViewModel } from "../../../models/ProduktdefinitionViewModel";
import { Diameterklass } from "../../matris/Diameterklass";
import { Produktmatris } from "../../matris/Produktmatris";
import { RadprisjusteringNumberCell } from "../../matris/cells/radprisjusteringNumberCell";

@Component({
    selector: "rask-vardematris",
    templateUrl: "./vardematris.component.html",
    styleUrls: ["./vardematris.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        HotDocComponent,
        TogglePanelComponent,
        SimpleSelectorComponent,
        InputNumberComponent,
        ButtonComponent
    ]
})
export class VardematrisComponent implements OnChanges, OnInit, OnDestroy {
    @Input() basData: IProduktdefinitionBasdata;
    form: UntypedFormGroup;
    hotDoc: HotDoc;
    hotDocRadprisjusteringar: HotDoc;
    isPrisradOpened = false;
    @Input() rootForm: UntypedFormGroup;
    @Input() viewModel: ProduktdefinitionViewModel;

    private readonly formBinder = inject(FormBinderService);
    private readonly formId = "produktinstruktioner-details-vardesmatris";
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);

    rebind = () => {
        this.hotDocRadprisjusteringar.rebind();
    };

    initMatrisSettings = () => {
        if (this.viewModel.vardematris.matrisdata.grid.onGridHeadersModified.indexOf(this.rebind) === -1) {
            this.viewModel.vardematris.matrisdata.grid.onGridHeadersModified.push(this.rebind);
        }

        if (this.isPrisradOpened) {
            this.refreshRadpris();
        }

        this.hotDoc = this.viewModel.vardematris.matrisdata.vardematris.getVardematris(this.raskModalService, this.rootForm, this.isPrisradOpened, () => {
            if (this.isPrisradOpened) {
                this.refreshRadpris();
            }
        });

        this.hotDocRadprisjusteringar = this.viewModel.vardematris.matrisdata.radprisjusteringarMatris
            .getRadprisjusteringar(this.raskModalService, this.rootForm, () => {
                this.onRadprisjustering();
            });
    };

    applyRadpriser = () => {
        this.viewModel.vardematris.matrisdata.vardematris.applyPrisrader();
        this.hotDoc.rebind();
    };

    applyRadpriserText() {
        return `Uppdatera ${this.viewModel.vardematris.matrisdata.vardematris.antalDiffandeRadpriser}st celler`;
    }

    copyRadpriser = () => {
        const excel = new DataExport();
        excel.addRow(row => {
            row.addCell("D \u2192");
            this.viewModel.vardematris.matrisdata.grid.diameterklasser.forEach(x => {
                row.addCell(x.startdiameter);
            });
        });

        excel.addRow(row => {
            row.addCell("Radpris");
            this.viewModel.vardematris.matrisdata.grid.diameterklasser.forEach(x => {
                row.addCell(x.radpris);
            });
        });

        excel.addEmptyRow();
        excel.addEmptyRow();

        excel.addRow(row => {
            row.addCell("\u2193 D / L \u2192");
            this.viewModel.vardematris.matrisdata.grid.langdklasser.forEach(x => {
                row.addCell(x.startlangd);
            });
        });

        this.viewModel.vardematris.matrisdata.grid.diameterklasser.filter(f => f.radprisJustering.length > 0).forEach(diameterklass => {
            excel.addRow(row => {
                row.addCell(diameterklass.startdiameter);
                this.viewModel.vardematris.matrisdata.grid.langdklasser.forEach(langdklass => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    const justering = diameterklass.radprisJustering.find(s => s.startlangd === langdklass.startlangd);
                    row.addCell(justering.justering);
                });
            });
        });

        excel.setToClipboard(() => this.toastService.add("Radpriser har kopierats till clipboard"));
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.viewModel && changes.viewModel.currentValue.vardematris !== changes.viewModel.previousValue?.vardematris) {
            this.initMatrisSettings();
            if (this.form) {
                this.formBinder.reBindForm(this.form, this.viewModel.vardematris);
            }
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initializeForm();
        this.form.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(debounceTime(20))
            .subscribe(() => {
                this.onRadprisjustering();
            });
    }

    onToggleRadpris = (enabled: boolean) => {
        this.isPrisradOpened = enabled;
        if (this.isPrisradOpened) {
            this.refreshRadpris();
        }

        this.hotDocRadprisjusteringar.rebind();
        this.viewModel.vardematris.matrisdata.vardematris.setVardematrisPrisrad(this.hotDoc, this.isPrisradOpened);
    };

    openCalculate = () => {
        type ActionType = "Ersätt" | "Multiplicera" | "Addera";
        const typeV: ActionType = "Ersätt";
        const input = new PromptInput(InputMode.none, {
            valueField: {
                label: "Värde",
                type: "number",
                value: null
            },
            selectField: {
                label: "Metod",
                type: "button-group",
                value: typeV,
                options: [
                    {
                        key: "Ersätt",
                        value: "Ersätt"
                    },
                    {
                        key: "Multiplicera",
                        value: "Multiplicera (%)"
                    },
                    {
                        key: "Addera",
                        value: "Addera"
                    }
                ]
            }
        });

        this.raskModalService.prompt("Beräkna påslag", input, "md").subscribe(response => {
            if (!response.cancel) {
                const value = response.fields.valueField?.value;
                const action = response.fields.selectField.value as ActionType;

                let method: (promptArg, pris) => number;
                switch (action) {
                    case "Addera":
                        method = (promptArg, pris) => promptArg + pris;
                        break;
                    case "Multiplicera":
                        method = (promptArg, pris) => promptArg / 100 * pris;
                        break;
                    case "Ersätt":
                        method = (promptArg, _value) => promptArg;
                        break;
                }

                if (this.hotDoc.selectedCells().length > 0) {
                    this.applyVardematris(method, value);
                    this.hotDoc.rebind();
                } else if (this.hotDocRadprisjusteringar.selectedCells().length > 0) {
                    this.applyPrisjustering(method, value);
                    this.hotDocRadprisjusteringar.rebind();
                }
            }
        });
    };

    openLangdDiameterSetup = () => {
        const input = new PromptInput(InputMode.none, {
            startFromField: {
                label: "Startvärde",
                type: "number",
                value: null,
                validators: [
                    {
                        expression: (val) => val >= 0,
                        message: "Värde måste vara större eller lika med 0"
                    }
                ]
            },
            raiseWithField: {
                label: "Öka med",
                type: "number",
                value: null,
                validators: [
                    {
                        expression: (val) => val > 0,
                        message: "Värde måste vara större än 0"
                    }
                ]
            },
            numberOfClassesField: {
                label: "Antal klasser",
                type: "number",
                value: null,
                validators: [
                    {
                        expression: (val) => val > 0,
                        message: "Värde måste vara större än 0"
                    }
                ]
            },
            selectField: {
                label: "Metod",
                type: "button-group",
                value: "Diameter",
                options: [
                    {
                        key: "Diameter",
                        value: "Diameter"
                    },
                    {
                        key: "Längd",
                        value: "Längd"
                    }
                ]
            }
        });

        this.raskModalService.prompt("Längd- och diameterhantering", input, "md").subscribe(response => {
            if (!response.cancel) {
                const numberOfClasses = response.fields.numberOfClassesField?.value;
                const startFrom = response.fields.startFromField?.value;
                const raiseWith = response.fields.raiseWithField?.value;
                const action = response.fields.selectField.value;

                const classes = [];
                if (_.isNumber(numberOfClasses) && _.isNumber(startFrom) && raiseWith > 0) {
                    let start = startFrom;
                    for (let i = 0; i < numberOfClasses; i++) {
                        classes.push(start);
                        start += raiseWith;
                    }
                }

                let maxValue: number;
                let method: (classValue: number) => void;
                switch (action) {
                    case "Längd":
                        maxValue = this.viewModel.headerData.maxlangd;
                        method = this.viewModel.vardematris.matrisdata.grid.addRow;
                        break;
                    case "Diameter":
                        maxValue = this.viewModel.headerData.maxdiameter;
                        method = this.viewModel.vardematris.matrisdata.grid.addColumn;
                        break;
                }

                classes
                    .filter(x => x <= maxValue)
                    .forEach(x => {
                        method(x);
                    });

                this.hotDoc.rebind();
            }
        });
    };

    setKapkriterie = (kapkriteria: string) => {
        const cells = this.hotDoc.selectedCells();
        this.viewModel.vardematris.matrisdata.vardematris.setKapkriteria(this.hotDoc, kapkriteria, cells);
    };

    private applyPrisjustering(method: (promptArg: any, pris: any) => number, value: any) {
        const cells = this.viewModel.vardematris.matrisdata.vardematris
            .getCellsFromSelection(this.hotDocRadprisjusteringar, this.hotDocRadprisjusteringar.selectedCells())
            .filter(p => p.cell instanceof RadprisjusteringNumberCell)
            .map(p => p.cell as RadprisjusteringNumberCell);

        cells.forEach(x => {
            const newValue = Math.max(Math.round(method(value, x.radprisjustering.justering)), 0);
            if (newValue !== x.radprisjustering.justering) {
                x.radprisjustering.justering = newValue;
                x.radprisjustering.dirty = true;
                this.rootForm.markAsDirty();
            }
        });
    }

    private applyVardematris(method: (promptArg: any, pris: any) => number, value: any) {
        const applyPm = (produktmatris: Produktmatris) => {
            const newPris = Math.max(Math.round(method(value, produktmatris.pris)), 0);
            if (newPris !== produktmatris.pris) {
                produktmatris.pris = newPris;
                produktmatris.markAsDirty();
                this.rootForm.markAsDirty();
            }
        };

        const applyPrisrad = (diameterklass: Diameterklass) => {
            const newVal = Math.max(Math.round(method(value, diameterklass.radpris)), 0);
            if (newVal !== diameterklass.radpris) {
                diameterklass.radpris = newVal;
                diameterklass.radprisDirty = true;
                this.rootForm.markAsDirty();
            }
        };

        this.viewModel.vardematris.matrisdata.vardematris.applyForPrismatris(this.hotDoc, this.hotDoc.selectedCells(), applyPm, applyPrisrad);
    }

    private onRadprisjustering() {
        if (this.isPrisradOpened) {
            // Lägger denna "utanför" för ökad responsitivitet i gridden
            setTimeout(() => {
                this.refreshRadpris();
                this.hotDoc.rebind();
            });
        }
    }

    private refreshRadpris() {
        this.viewModel.vardematris.matrisdata.vardematris.refreshPrisradValue(
            this.viewModel.vardematris.radprisLangdkorrektion === "Absolut",
            this.viewModel.vardematris.radprisUtgangsvarde);
    }

    private initializeForm() {
        this.form = this.formBinder.CreateGroup(this.viewModel.vardematris);
        this.rootForm.addControl(this.formId, this.form);
        if (!this.viewModel.canEdit) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }
}
